import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LangProvider, useLang } from './i18n';
import { useEffect } from 'react';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HelpButton from './components/HelpButton';
import WhatsAppPopup from './components/WhatsAppPopup';
import HomePage from './pages/HomePage';
import LibroPage from './pages/LibroPage';
import CursoOnlinePage from './pages/CursoOnlinePage';
import ClasesPage from './pages/ClasesPage';
import ContactanosPage from './pages/ContactanosPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import NosotrosPage from './pages/NosotrosPage';
import PoliticaPage from './pages/PoliticaPage';
import TerminosPage from './pages/TerminosPage';
import CookiesPage from './pages/CookiesPage';
import DevolucionPage from './pages/DevolucionPage';
import AvisoLegalPage from './pages/AvisoLegalPage';
import NotFoundPage from './pages/NotFoundPage';
import { SITE_URL } from './data/siteData';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = canonical.href;
  }, [pathname]);

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const timer = window.setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView(), 80);
    return () => window.clearTimeout(timer);
  }, [pathname, hash]);

  useEffect(() => {
    let io;
    const t = setTimeout(() => {
      io = new IntersectionObserver(
        (entries) => entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('section-visible'); io.unobserve(e.target); }
        }),
        { threshold: 0.07, rootMargin: '0px 0px -48px 0px' }
      );
      document.querySelectorAll(
        '.section-pad, .page-hero, .nosotros-hero, .curso-hero, .policy-hero, .dark-stats-banner, .stats-row, .press-proof-section, .client-proof-section'
      ).forEach(el => { el.classList.add('reveal-section'); io.observe(el); });
    }, 80);
    return () => { clearTimeout(t); if (io) io.disconnect(); };
  }, [pathname]);

  return null;
}

function SpanishOnlyNotice() {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const bare = pathname.replace(/^\/ca/, '') || '/';
  const spanishOnly = /^\/(blog\/|politica-privacidad|terminos|cookies|devolucion|aviso-legal)/.test(bare);
  if (lang !== 'ca' || !spanishOnly) return null;
  return <p className="lang-notice" role="note">{t('', 'Aquest contingut només està disponible en castellà.')}</p>;
}

function PrecioRedirect() {
  const { path } = useLang();
  return <Navigate to={path('/contacto')} replace />;
}

const ROUTES = [
  ['/', <HomePage />],
  ['/curso-online', <CursoOnlinePage />],
  ['/clases', <ClasesPage />],
  ['/precio', <PrecioRedirect />],
  ['/contacto', <ContactanosPage />],
  ['/blog', <BlogPage />],
  ['/blog/:slug', <BlogPostPage />],
  ['/nosotros', <NosotrosPage />],
  ['/politica-privacidad', <PoliticaPage />],
  ['/terminos', <TerminosPage />],
  ['/cookies', <CookiesPage />],
  ['/devolucion', <DevolucionPage />],
  ['/aviso-legal', <AvisoLegalPage />],
  ['/libro', <LibroPage />],
];

function Layout() {
  return (
    <div className="app">
      <div className="ambient-dot-field" aria-hidden="true" />
      <Navbar />
      <main>
        <ScrollToTop />
        <SpanishOnlyNotice />
        <Routes>
          {ROUTES.map(([path, element]) => <Route key={path} path={path} element={element} />)}
          {ROUTES.map(([path, element]) => <Route key={`ca${path}`} path={path === '/' ? '/ca' : `/ca${path}`} element={element} />)}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <HelpButton />
      <WhatsAppPopup />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <Layout />
      </LangProvider>
    </BrowserRouter>
  );
}
